import { useContext, useEffect, useRef } from "react";
import { REMINDER_STATE, SWIVU_HOME_VIEW, VIEW_TYPES } from "../../other/constants/OtherKeys.jsx";
import AuthContext from "../context/authContext.jsx";
import BryntumContext from "../context/bryntumContext.jsx";
import { debounce } from "../../other/utils/otherUtils.jsx";

// Todo: test with the board alerts
const useDisplayHomeModal = () => {
  const user = useContext(AuthContext);
  const { isAppLoaded, selectedBoard, boardManager, project, viewManager } = useContext(BryntumContext);
  const { setDisplayAuthForm, isNewSessionRef } = useContext(AuthContext);
  // we want to display swivu home modal only if we didn't already display it, so when switching board it should not appear
  const isSwivuHomeDisplayed = useRef(false);

  // keeping it in a separate useEffect as switching board triggers the board alert popup before board gets switched
  useEffect(() => {
    // Board Alert Banner popup handling
    if (project && isAppLoaded) {

      const displayIncentiveExecutionInfoIfAny = (e) => {
        // display only on page load and when there is a new incentive execution completed
        const record = e?.records?.[0];
        if (e && (record?.alertTag !== "Incentive Evaluation" || record?.isSeen || record.description !== "Completed")) return;

        const unseenIncentivesExecutionOfMine = project.reminderStore.query((reminder) =>
          reminder.alertTag === "Incentive Evaluation" &&
          reminder.reminderState === REMINDER_STATE.NOT_SEEN && // display only if reminder is sent
          reminder.isIAmOneOfRecipient &&
          reminder.description === "Completed" &&
          !reminder.isSeen
        ).sort((a, b) => new Date(a.alertDate) - new Date(b.alertDate)); // sort to display recent alerts first
        if (unseenIncentivesExecutionOfMine.length) {
          isSwivuHomeDisplayed.current = true;
          setDisplayAuthForm({
            type: SWIVU_HOME_VIEW.incentiveExecution,
            data: {
              unseenIncentivesExecutionOfMine,
              project,
              onShowPointsClicked(incentiveIds) {
                viewManager.selectView(
                  viewManager.getDefaultViewByType(VIEW_TYPES.incentivePoints),
                  { canNavigate: true, forceLoadView: true }, { filters: { incentiveIds }}
                );
              }
            }
          });
        }
      }

      /* Using a debounced function here for better performance when updating react state */
      const displayBoardAlerts = debounce((preventDisplayIncentiveExecutionModal) => {
        const unseenBoardAlertsOfMine = project.reminderStore.query((reminder) =>
          // for the old record such as the mention records we don't have notify setting for that so we expect the record return below can be null
          project.notifyPreferenceStore.find(userNotifyRecord => userNotifyRecord.notifyType === reminder.alertType)?.interupt &&
          reminder.reminderState === REMINDER_STATE.NOT_SEEN && // display only if reminder is sent
          reminder.isIAmOneOfRecipient &&
          !reminder.isSeen
        , true).sort((a, b) => new Date(a.alertDate) - new Date(b.alertDate)); // sort to display recent alerts first

        if (unseenBoardAlertsOfMine.length) {
          isSwivuHomeDisplayed.current = true
          setDisplayAuthForm({
            type: SWIVU_HOME_VIEW.boardAlerts,
            data: { unseenBoardAlertsOfMine }
          });
        } else if (!preventDisplayIncentiveExecutionModal) {
          // only display if not displaying board alert popup
          displayIncentiveExecutionInfoIfAny();
        }
      });

      // display board alert on page load
      displayBoardAlerts();

      // display board alert popup on receiving through socket or when marking the board alert banner as "Mark as seen"
      const destroyChangeListener = project.reminderStore.on("change", (e) => {
        displayBoardAlerts(true);
        if (e.action === "add") {
          displayIncentiveExecutionInfoIfAny(e);
        }
      })


      const destroyNotifyPreferenceListener = project.notifyPreferenceStore.on("update", ({ changes }) => {
        /* Display alerts again if interupts propery is changed for records in the notify preference store */
        if ("interupt" in changes) displayBoardAlerts();
      })

      return () => {
        destroyChangeListener();
        destroyNotifyPreferenceListener();
      };
    }
  }, [project, isAppLoaded]);

  useEffect(() => {
    // Swivu Home Modal popup handling
    const canDisplaySwivuHomeModal = isNewSessionRef.current && isAppLoaded && user.user && (!isSwivuHomeDisplayed.current || selectedBoard?.record?.isSandbox);
    if (canDisplaySwivuHomeModal) {
      const homeModalData = {
        selectedBoard: selectedBoard.record,
        boards: boardManager.boardNameStore.records,
        recentInvites: user.user.recent_invites,
        onBoardSelected: (board) => boardManager.changeSelectedBoard(board),
      };
      const isSandboxBoard = user.user.isDemoUser || selectedBoard.record.isSandbox;
      setDisplayAuthForm({
        type: isSandboxBoard ? SWIVU_HOME_VIEW.sandbox: SWIVU_HOME_VIEW.welcome,
        data: homeModalData
      });
      isSwivuHomeDisplayed.current = true;
      isNewSessionRef.current = false;
    }

    if (!user?.user) isSwivuHomeDisplayed.current = false;
  }, [isAppLoaded, selectedBoard, boardManager.boardNameStore, user.user, project])
};

export default useDisplayHomeModal;
